import {
  Box,
  Paper,
  Chip,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Typography,
  Autocomplete,
  TextField,
  Card,
  CardContent,
  Divider,
  Stack,
  Grid
} from '@mui/material';
import {
  Clear as ClearIcon,
  Inventory2Outlined as ProductIcon,
  PersonOutlined as ClientIcon,
  WarehouseOutlined as StockIcon,
  LocalShippingOutlined as ProviderIcon,
  CategoryOutlined as ActivityTypeIcon,
  EventOutlined as DateRangeIcon,
  AddCircleOutline as AddIcon,
  QrCodeOutlined as SerialNumberIcon,
} from '@mui/icons-material';
import type { Product } from '../../../types/product';
import type { Contact } from '../../../types/contact';
import type { Stock } from '../../../types/stock';
import type { ActivityType } from '../../../types/activityType';
import { formatContactFullName } from '../../../utils/contactName';
import { hasActiveFilters } from '../types';

interface ActivityFiltersProps {
  show: boolean;
  filterData: {
    products: Product[];
    clients: Contact[];
    stocks: Stock[];
    providers: Contact[];
    activityTypes: ActivityType[];
    availableActivityTypes: ActivityType[];
    serialNumber: string;
    fromDate?: string;
    toDate?: string;
    showOnlyActive: boolean;
  };
  filterActions: {
    handleSelectProducts: () => void;
    handleRemoveProduct: (productId: number) => void;
    handleSelectClients: () => void;
    handleRemoveClient: (clientId: number) => void;
    handleSelectStocks: () => void;
    handleRemoveStock: (stockId: number) => void;
    handleSelectProviders: () => void;
    handleRemoveProvider: (providerId: number) => void;
    handleActivityTypesChange: (activityTypes: ActivityType[]) => void;
    setSerialNumber: (sn: string) => void;
    setFromDate: (date: string | undefined) => void;
    setToDate: (date: string | undefined) => void;
    setShowOnlyActive: (value: boolean) => void;
    handleClearFilters: () => void;
  };
}

interface FilterCardProps {
  icon: React.ReactNode;
  title: string;
  items: Array<{ id: number; name: string }>;
  color: 'primary' | 'secondary' | 'info' | 'warning' | 'success';
  onRemove: (id: number) => void;
  onAdd: () => void;
  emptyText: string;
}

function FilterCard({
  icon,
  title,
  items,
  color,
  onRemove,
  onAdd,
  emptyText,
}: FilterCardProps) {
  return (
    <Card 
      variant="outlined" 
      sx={{ 
        height: '100%',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.main'
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box sx={{ color: `${color}.main`, display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            ({items.length})
          </Typography>
        </Stack>
        
        <Divider sx={{ mb: 1.5 }} />
        
        <Box sx={{ minHeight: '60px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {items.map((item) => (
                <Chip
                  key={item.id}
                  label={item.name}
                  size="small"
                  onDelete={() => onRemove(item.id)}
                  color={color}
                  variant="filled"
                  sx={{ 
                    fontWeight: 500,
                    '& .MuiChip-deleteIcon': {
                      fontSize: '16px'
                    }
                  }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {emptyText}
            </Typography>
          )}
          
          <Button
            size="small"
            variant="text"
            startIcon={<AddIcon />}
            onClick={onAdd}
            fullWidth
            sx={{ 
              mt: 'auto',
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            {items.length > 0 ? 'Додати ще' : 'Вибрати'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function ActivityFiltersPanel({
  show,
  filterData,
  filterActions,
}: ActivityFiltersProps) {
  const hasFilters = hasActiveFilters(filterData);

  return (
    <Collapse in={show}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ mb: hasFilters ? 2 : 0 }}>
          <Grid container spacing={2}>
            {/* Product Filter */}
            <Grid item xs={12} sm={6} md={4}>
              <FilterCard
                icon={<ProductIcon />}
                title="Продукти"
                items={filterData.products}
                color="primary"
                onRemove={filterActions.handleRemoveProduct}
                onAdd={filterActions.handleSelectProducts}
                emptyText="Фільтр не застосовано"
              />
            </Grid>

            {/* Client Filter */}
            <Grid item xs={12} sm={6} md={4}>
              <FilterCard
                icon={<ClientIcon />}
                title="Клієнти"
                items={filterData.clients.map((c) => ({ id: c.id, name: formatContactFullName(c) }))}
                color="secondary"
                onRemove={filterActions.handleRemoveClient}
                onAdd={filterActions.handleSelectClients}
                emptyText="Фільтр не застосовано"
              />
            </Grid>

            {/* Stock Filter */}
            <Grid item xs={12} sm={6} md={4}>
              <FilterCard
                icon={<StockIcon />}
                title="Склади"
                items={filterData.stocks}
                color="info"
                onRemove={filterActions.handleRemoveStock}
                onAdd={filterActions.handleSelectStocks}
                emptyText="Фільтр не застосовано"
              />
            </Grid>

            {/* Provider Filter */}
            <Grid item xs={12} sm={6} md={4}>
              <FilterCard
                icon={<ProviderIcon />}
                title="Постачальники"
                items={filterData.providers.map((p) => ({ id: p.id, name: formatContactFullName(p) }))}
                color="warning"
                onRemove={filterActions.handleRemoveProvider}
                onAdd={filterActions.handleSelectProviders}
                emptyText="Фільтр не застосовано"
              />
            </Grid>

            {/* Serial Number Filter */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box sx={{ color: 'secondary.main', display: 'flex', alignItems: 'center' }}>
                      <SerialNumberIcon />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                      Серійний номер
                    </Typography>
                  </Stack>

                  <Divider sx={{ mb: 1.5 }} />

                  <TextField
                    size="small"
                    fullWidth
                    value={filterData.serialNumber}
                    onChange={(e) => filterActions.setSerialNumber(e.target.value)}
                    placeholder="Введіть точний серійний номер"
                    helperText="Необхідно ввести повний серійний номер"
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Activity Type Filter */}
            <Grid item xs={12} sm={12} md={8}>
              <Card 
                variant="outlined"
                sx={{ 
                  height: '100%',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
                      <ActivityTypeIcon />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                      Типи активностей
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      ({filterData.activityTypes.length})
                    </Typography>
                  </Stack>
                  
                  <Divider sx={{ mb: 1.5 }} />
                  
                  <Autocomplete
                    multiple
                    options={filterData.availableActivityTypes}
                    value={filterData.activityTypes}
                    onChange={(_, newValue) => filterActions.handleActivityTypesChange(newValue)}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Почніть вводити для пошуку..."
                        size="small"
                        variant="outlined"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name}
                          {...getTagProps({ index })}
                          size="small"
                          color="success"
                          variant="filled"
                          sx={{ fontWeight: 500 }}
                        />
                      ))
                    }
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Date Range Filter */}
            <Grid item xs={12} sm={12} md={4}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box sx={{ color: 'info.main', display: 'flex', alignItems: 'center' }}>
                      <DateRangeIcon />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                      Період
                    </Typography>
                  </Stack>

                  <Divider sx={{ mb: 1.5 }} />

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      type="date"
                      label="Від"
                      size="small"
                      fullWidth
                      value={filterData.fromDate ?? ''}
                      onChange={(e) => filterActions.setFromDate(e.target.value || undefined)}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <TextField
                      type="date"
                      label="До"
                      size="small"
                      fullWidth
                      value={filterData.toDate ?? ''}
                      onChange={(e) => filterActions.setToDate(e.target.value || undefined)}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Show Only Active */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, width: '100%' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={filterData.showOnlyActive}
                        onChange={(e) => filterActions.setShowOnlyActive(e.target.checked)}
                      />
                    )}
                    label="Показувати лише активні"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Clear Filters Button */}
        {hasFilters && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="error"
              size="medium"
              startIcon={<ClearIcon />}
              onClick={filterActions.handleClearFilters}
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                px: 3
              }}
            >
              Очистити всі фільтри
            </Button>
          </Box>
        )}
      </Paper>
    </Collapse>
  );
}
